
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

interface PageHeroProps {
  breadcrumb: string;
  label?: string;
  title: ReactNode;
  description?: string;
  badge?: string;
  bgImage?: string;
  children?: ReactNode;
}

export default function PageHero({ breadcrumb, label, title, description, badge, bgImage, children }: PageHeroProps) {
  return (
    <section className="relative bg-[#1A3A22] overflow-hidden">
      {/* Background image */}
      {bgImage && (
        <>
          <div className="absolute inset-0 bg-cover bg-center scale-[1.02]" style={{ backgroundImage: `url('${bgImage}')` }} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A22]/95 via-[#1A3A22]/78 to-[#1A3A22]/36" />
        </>
      )}
      {/* Dot texture */}
      <div
        className="absolute inset-0 opacity-[0.045]"
        style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '24px 24px' }}
      />
      <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#5EC97A]/10 blur-3xl" />
      <div className="absolute right-[18%] bottom-0 h-px w-1/2 bg-gradient-to-r from-transparent via-[#5EC97A]/60 to-transparent" />
      {/* Left accent stripe */}
      <div className="absolute left-0 top-0 w-1 h-full bg-[#2D7F3E]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-white/50 mb-8 font-medium">
          <Link to="/" className="hover:text-white/70 transition-colors">Home</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-white/70">{breadcrumb}</span>
        </nav>

        <div className="max-w-2xl">
          {label && <p className="section-label mb-4" style={{ color: '#5EC97A' }}>{label}</p>}

          {badge && (
            <div className="inline-flex items-center gap-2 bg-[#2D7F3E]/40 border border-[#5EC97A]/30 text-[#5EC97A] text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-5">
              <i className="ri-shield-check-fill" />
              {badge}
            </div>
          )}

          <h1 className="font-serif font-normal text-white leading-tight mb-5"
            style={{ fontSize: 'clamp(2rem, 5vw, 3.25rem)', color: 'white' }}>
            {title}
          </h1>

          {description && (
            <p className="text-white/75 text-base md:text-lg leading-relaxed mb-8 max-w-2xl">{description}</p>
          )}

          {children && <div className="flex flex-wrap gap-3">{children}</div>}
        </div>
      </div>
    </section>
  );
}
